/**
 * Base Repository Pattern — generic CRUD abstraction untuk SQLite cache.
 *
 * Prinsip (docs/09-Roadmap.md S5, docs/01-SRS.md §11):
 * - UI tidak pernah menyentuh SQL langsung — semua akses data melalui Repository.
 * - Satu pola, implementasi per-entity di subclass.
 * - Siap untuk dual-write (SQLite + Supabase) di sprint S14.
 *
 * Generic type T = tipe entity (mis. ActivityCache, WorkspaceCache).
 * Setiap subclass wajib mengimplementasi:
 *   - tableName: nama tabel cache
 *   - mapRow(row): konversi raw DB row → typed entity
 */

import type { SqlValue } from 'sql.js';
import { requireDatabase, type SqliteDatabase } from './sqlite-client';

// ---------------------------------------------------------------------------
// Base Entity Types
// ---------------------------------------------------------------------------

/** Entity dengan primary key id (minimum requirement untuk BaseRepository). */
export interface BaseEntity {
  id: string;
}

/** Entity dengan timestamp audit lengkap (created_at + updated_at). */
export interface AuditableEntity extends BaseEntity {
  created_at: string;
  updated_at: string;
}

/** Entity dengan soft delete support (tambah deleted_at). */
export interface SoftDeletableEntity extends AuditableEntity {
  deleted_at: string | null;
}

// ---------------------------------------------------------------------------
// Base Repository
// ---------------------------------------------------------------------------

/**
 * Abstract base class untuk semua repository cache.
 *
 * Menyediakan operasi CRUD generik. Subclass menambahkan:
 * - create(data): mapping input → kolom, lalu insert
 * - update(id, changes): mapping changes → SET clause
 * - Query khusus entity (mis. findByWorkspace, findByDateRange)
 */
export abstract class BaseRepository<T extends BaseEntity> {
  /** Nama tabel cache (mis. 'activity_cache'). */
  protected abstract readonly tableName: string;

  /** Konversi raw DB row (Record<string, unknown>) → typed entity T. */
  protected abstract mapRow(row: Record<string, unknown>): T;

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  /** Ambil instance database (throw jika belum di-init). */
  protected get db(): SqliteDatabase {
    return requireDatabase();
  }

  /** Generate UUID v4 untuk primary key baru. */
  protected generateId(): string {
    return crypto.randomUUID();
  }

  /** Timestamp ISO 8601 UTC saat ini. */
  protected now(): string {
    return new Date().toISOString();
  }

  // -------------------------------------------------------------------------
  // Generic Read Operations
  // -------------------------------------------------------------------------

  /** Ambil entity berdasarkan ID. Return null jika tidak ditemukan. */
  async findById(id: string): Promise<T | null> {
    const row = this.db.queryOne<Record<string, unknown>>(
      `SELECT * FROM ${this.tableName} WHERE id = ?`,
      [id],
    );
    return row ? this.mapRow(row) : null;
  }

  /**
   * Ambil semua entity yang cocok dengan filter (equality match).
   * Filter dengan value undefined diabaikan.
   *
   * @example
   * findAll({ workspace_id: 'ws-1', status: 'draft' })
   * // → SELECT * FROM activity_cache WHERE workspace_id = ? AND status = ?
   */
  async findAll(filter?: Record<string, SqlValue | undefined | null>): Promise<T[]> {
    let sql = `SELECT * FROM ${this.tableName}`;
    const params: SqlValue[] = [];

    if (filter) {
      const entries = Object.entries(filter).filter(([, v]) => v !== undefined && v !== null);
      if (entries.length > 0) {
        const conditions = entries.map(([key, value]) => {
          params.push(value as SqlValue);
          return `${key} = ?`;
        });
        sql += ` WHERE ${conditions.join(' AND ')}`;
      }
    }

    const rows = this.db.queryAll<Record<string, unknown>>(sql, params);
    return rows.map((row) => this.mapRow(row));
  }

  /** Hitung jumlah entity yang cocok dengan filter. */
  async count(filter?: Record<string, SqlValue | undefined | null>): Promise<number> {
    let sql = `SELECT COUNT(*) as count FROM ${this.tableName}`;
    const params: SqlValue[] = [];

    if (filter) {
      const entries = Object.entries(filter).filter(([, v]) => v !== undefined && v !== null);
      if (entries.length > 0) {
        const conditions = entries.map(([key, value]) => {
          params.push(value as SqlValue);
          return `${key} = ?`;
        });
        sql += ` WHERE ${conditions.join(' AND ')}`;
      }
    }

    const result = this.db.queryOne<{ count: number }>(sql, params);
    return result?.count ?? 0;
  }

  // -------------------------------------------------------------------------
  // Generic Write Helpers (protected — subclass memanggil dari create/update)
  // -------------------------------------------------------------------------

  /**
   * INSERT baris baru.
   * @param data Object column→value (mis. { id, title, ... }).
   */
  protected insertRow(data: Record<string, SqlValue>): void {
    const columns = Object.keys(data);
    const values = columns.map((col) => data[col] as SqlValue);
    const placeholders = columns.map(() => '?').join(', ');
    this.db.run(
      `INSERT INTO ${this.tableName} (${columns.join(', ')}) VALUES (${placeholders})`,
      values,
    );
  }

  /**
   * UPDATE kolom berdasarkan ID.
   * @param id Primary key entity.
   * @param changes Object column→newValue (mis. { title: 'baru', updated_at: ... }).
   */
  protected updateRow(id: string, changes: Record<string, SqlValue>): void {
    const entries = Object.entries(changes);
    if (entries.length === 0) return;
    const setClause = entries.map(([key]) => `${key} = ?`).join(', ');
    const values = entries.map(([, value]) => value);
    this.db.run(`UPDATE ${this.tableName} SET ${setClause} WHERE id = ?`, [...values, id]);
  }

  /**
   * Soft delete — set deleted_at = now(), updated_at = now().
   * Hanya untuk tabel yang memiliki kolom deleted_at.
   */
  async softDelete(id: string): Promise<void> {
    const now = this.now();
    this.db.run(`UPDATE ${this.tableName} SET deleted_at = ?, updated_at = ? WHERE id = ?`, [
      now,
      now,
      id,
    ]);
  }

  /**
   * Hard delete — hapus baris permanen.
   * Gunakan dengan hati-hati (docs/04-Database.md DM-02: soft delete wajib
   * untuk entitas bisnis). Hard delete hanya untuk junction tables.
   */
  async hardDelete(id: string): Promise<void> {
    this.db.run(`DELETE FROM ${this.tableName} WHERE id = ?`, [id]);
  }
}
